/**
 * Day 11 — Calendar Operational Charts aggregation helpers
 *
 * Pure functions over `filteredShifts` already materialized in
 * RosterCalendarPage.jsx. No API calls, no mutation.
 *
 * Group-session policy (documented, per Day 11 prompt requirement):
 *   - Group sessions (`shift._isGroupSession === true`) are INCLUDED in:
 *       groupByStatus, groupByDate, groupByService
 *     because they represent real scheduled time users need to see.
 *   - They are EXCLUDED from groupByStaff because a group session is not
 *     actually "assigned" to one staff member; counting them per staff
 *     would double-count attendees.
 *   - Every staff counted in groupByStaff comes from `shift.assigned_staff[]`
 *     of a normal shift.
 */

function _pad(n) { return n < 10 ? `0${n}` : `${n}`; }

export function groupByStatus(shifts) {
  const counts = new Map();
  for (const s of shifts || []) {
    const k = s?.status || 'UNKNOWN';
    counts.set(k, (counts.get(k) || 0) + 1);
  }
  return [...counts.entries()]
    .map(([status, count]) => ({ status, count }))
    .sort((a, b) => b.count - a.count);
}

export function groupByDate(shifts) {
  const counts = new Map();
  for (const s of shifts || []) {
    const d = s?.date;
    if (!d) continue;
    counts.set(d, (counts.get(d) || 0) + 1);
  }
  if (counts.size === 0) return [];
  const keys = [...counts.keys()].sort();
  // Dense daily fill between min and max, cap at 31 days for chart readability
  const [y0, m0, d0] = keys[0].split('-').map(Number);
  const [y1, m1, d1] = keys[keys.length - 1].split('-').map(Number);
  const start = new Date(y0, (m0 || 1) - 1, d0 || 1);
  const end = new Date(y1, (m1 || 1) - 1, d1 || 1);
  const out = [];
  const cursor = new Date(start);
  while (cursor <= end) {
    const key = `${cursor.getFullYear()}-${_pad(cursor.getMonth() + 1)}-${_pad(cursor.getDate())}`;
    out.push({
      date: key,
      label: cursor.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
      weekday: cursor.toLocaleDateString(undefined, { weekday: 'short' }),
      count: counts.get(key) || 0,
    });
    cursor.setDate(cursor.getDate() + 1);
  }
  return out.length > 31 ? out.slice(out.length - 31) : out;
}

export function groupByStaff(shifts) {
  // Exclude group sessions (they aren't bound to a single staff member).
  const counts = new Map();
  for (const s of shifts || []) {
    if (s?._isGroupSession) continue;
    const list = Array.isArray(s?.assigned_staff) ? s.assigned_staff : [];
    for (const staff of list) {
      const name = staff?.name || 'Unknown';
      counts.set(name, (counts.get(name) || 0) + 1);
    }
  }
  return [...counts.entries()]
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 10);
}

export function groupByService(shifts) {
  const counts = new Map();
  for (const s of shifts || []) {
    const name = s?.service_name || s?.service_code || 'Unspecified';
    counts.set(name, (counts.get(name) || 0) + 1);
  }
  return [...counts.entries()]
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);
}

export function computeOpsMetrics(shifts) {
  const list = shifts || [];
  const total = list.length;
  const open = list.filter(s => s?.status === 'OPEN').length;
  const partial = list.filter(s => s?.status === 'PARTIALLY_FILLED').length;
  const coverageRisk = open + partial;
  const uniqueDays = new Set(list.map(s => s?.date).filter(Boolean)).size;
  const uniqueStaff = new Set(
    list
      .filter(s => !s?._isGroupSession)
      .flatMap(s => (s?.assigned_staff || []).map(st => st?.id || st?.name))
      .filter(Boolean),
  ).size;
  return {
    total,
    coverageRisk,
    coverageRiskPct: total ? Math.round((coverageRisk / total) * 100) : 0,
    uniqueDays,
    uniqueStaff,
  };
}

// Hex colours aligned with /app/frontend/src/lib/shiftStatusMeta.js badge classes.
// These drive the Coverage Breakdown donut and avoid re-declaring the palette elsewhere.
export const STATUS_HEX = {
  OPEN: '#dc2626',              // red-600
  PARTIALLY_FILLED: '#d97706',  // amber-600
  ASSIGNED: '#16a34a',          // green-600
  CONFIRMED: '#2563eb',         // blue-600
  IN_PROGRESS: '#7c3aed',       // violet-600 (matches purple-800 text)
  COMPLETED: '#6b7280',         // gray-500
  CANCELLED: '#9ca3af',         // gray-400
  NO_SHOW: '#e11d48',           // rose-600
  INVOICED: '#0284c7',          // sky-600
  UNKNOWN: '#94a3b8',           // slate-400
};

export function statusLabel(status) {
  const map = {
    OPEN: 'Open',
    PARTIALLY_FILLED: 'Partial',
    ASSIGNED: 'Assigned',
    CONFIRMED: 'Confirmed',
    IN_PROGRESS: 'In progress',
    COMPLETED: 'Completed',
    CANCELLED: 'Cancelled',
    NO_SHOW: 'No show',
    INVOICED: 'Invoiced',
    UNKNOWN: 'Unknown',
  };
  return map[status] || status;
}
